import React from "react"
import { useFormContext } from "react-hook-form"
import { twMerge } from "tailwind-merge"
import { isNil } from "~/src/lib/any"
import { Country } from "~/src/lib/country"
import { FormError } from "./FormError"

type AddressError = {
  addressCountry?: string
  address1?: string
  address2?: string
  addressCity?: string
  addressState?: string
  addressZip?: string
  addressPhone?: string
}

export type AddressFieldsProps = {
  serverErrors?: AddressError
  formErrors?: object
  internationalAllowed?: boolean
  restrictedCountryCodes?: string[]
  fieldNamePrefix: string
  disabled?: boolean
  className?: string
}

/**
 * Common address fieldset. Changing country changes available states.
 *  Needs to be nested under <FormProvider></FormProvider> for access to useFormContext()
 *
 * @param props
 * @param props.serverErrors Any errors being returned by the backend.
 * @param props.formErrors Frontend validation errors
 * @param props.internationalAllowed Whether country selection is allowed. Defaults to US if false.
 * @param props.restrictedCountries Array of 2 letter country codes. If provided, only these countries can be selected in the dropdown.  Empty array means all countries can be selected.
 * @param props.fieldNamePrefix String that will be added to the front of all input/select names
 * @param props.disabled Disable all fields
 * @param props.className Classes for fieldset
 * @returns
 * @example
 * <FormProvider>
 *   <Bee.AddressFields
 *     serverErrors={{ addressCity: "too long" }}
 *     formErrors={errors["quick_send"]?.["order_recipients_attributes"]?.[0]?.["address_attributes"]}
 *     internationalAllowed={false}
 *     restrictedCountries={["US", "CA"]}
 *     fieldNamePrefix="quick_send[order_recipients_attributes][0][address_attributes]"
 *     disabled={false}
 *   />
 * </FormProvider>
 */
export function AddressFields(props: AddressFieldsProps) {
  const {
    className,
    internationalAllowed,
    serverErrors,
    formErrors,
    fieldNamePrefix,
    disabled = false,
    restrictedCountryCodes = [],
  } = props

  const {
    register,
    watch,
    formState: { isDirty },
  } = useFormContext()

  const watchCountry = watch(`${fieldNamePrefix}[address_country]`, "US")
  const country = Country.fromAlpha2(watchCountry)

  return (
    <fieldset className={twMerge("flex flex-col gap-4", className)}>
      {/* Country */}
      <section className="flex flex-col w-full">
        <div className="w-full">
          <h2 className="font-medium">Country</h2>
          <FormError
            name="Country"
            initialError={serverErrors?.addressCountry}
            fieldIsDirty={isDirty}
            error={formErrors?.["address_country"]?.message}
          />
          <select
            className="w-full border border-gray-300 rounded"
            disabled={disabled || !internationalAllowed}
            {...register(`${fieldNamePrefix}[address_country]`, {
              required: { value: !disabled, message: "required" },
            })}
          >
            {Country.allPriorityFirst().map((c) => (
              <option
                key={c.alpha2}
                value={c.alpha2}
                disabled={
                  Country.disabledCountries().includes(c.alpha2) ||
                  (restrictedCountryCodes.length != 0 && !restrictedCountryCodes.includes(c.alpha2))
                }
              >
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </section>
      {/* Street Address */}
      <section className="flex flex-col w-full">
        <div className="w-full">
          <h2 className="font-medium">Street address</h2>
          <FormError
            name="Street address"
            initialError={serverErrors?.address1}
            fieldIsDirty={isDirty}
            error={formErrors?.["address_1"]?.message}
          />
          <input
            className="w-full border border-gray-300 rounded"
            disabled={disabled}
            {...register(`${fieldNamePrefix}[address_1]`, {
              required: { value: !disabled, message: "required" },
            })}
          />
        </div>
      </section>
      {/* Address line 2 */}
      <section className="flex flex-col w-full">
        <div className="w-full">
          <h2 className="font-medium">
            Address line 2 <span className="text-sm text-gray-500 inline"> - Optional</span>
          </h2>
          <FormError
            name="Address line 2"
            initialError={serverErrors?.address2}
            fieldIsDirty={isDirty}
            error={formErrors?.["address_2"]?.message}
          />
          <input
            className="w-full border border-gray-300 rounded"
            disabled={disabled}
            {...register(`${fieldNamePrefix}[address_2`)}
          />
        </div>
      </section>
      {/* City */}
      <section className="md:grid md:grid-cols-[2fr_1fr_1fr] flex flex-col gap-4 w-full ">
        <div>
          <h2 className="font-medium">City</h2>
          <FormError
            name="City"
            initialError={serverErrors?.addressCity}
            fieldIsDirty={isDirty}
            error={formErrors?.["address_city"]?.message}
          />
          <input
            className="w-full border border-gray-300 rounded"
            disabled={disabled}
            {...register(`${fieldNamePrefix}[address_city]`, {
              required: { value: !disabled, message: "required" },
            })}
          />
        </div>
        {/* State */}
        {country instanceof Error || isNil(country.states) ? (
          <></>
        ) : (
          <div>
            <h2 className="font-medium">State</h2>
            <FormError
              name="State"
              initialError={serverErrors?.addressState}
              fieldIsDirty={isDirty}
              error={formErrors?.["address_state"]?.message}
            />
            <select
              className="w-full border border-gray-300 rounded"
              disabled={disabled}
              {...register(`${fieldNamePrefix}[address_state]`, {
                required: { value: !disabled && country.isStateRequired(), message: "required" },
              })}
            >
              {country.states.map((s) => (
                <option key={s.alpha2} value={s.alpha2}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Zip */}
        <div>
          <h2 className="font-medium">Zip Code</h2>
          <FormError
            name="Zip Code"
            initialError={serverErrors?.addressZip}
            fieldIsDirty={isDirty}
            error={formErrors?.["address_zip"]?.message}
          />
          <input
            className="w-full border border-gray-300 rounded"
            disabled={disabled}
            {...register(`${fieldNamePrefix}[address_zip]`, {
              required: { value: !disabled, message: "required" },
            })}
          />
        </div>
        {/* Phone Number */}
        {!(country instanceof Error) && country.isInternational() ? (
          <div>
            <h2 className="font-medium">Phone Number</h2>
            <FormError
              name="Phone Number"
              initialError={serverErrors?.addressPhone}
              fieldIsDirty={isDirty}
              error={formErrors?.["address_phone"]?.message}
            />
            <input
              className="w-full border border-gray-300 rounded"
              disabled={disabled}
              {...register(`${fieldNamePrefix}[address_phone]`, {
                required: { value: !disabled, message: "required" },
              })}
            />
          </div>
        ) : (
          <></>
        )}
      </section>
    </fieldset>
  )
}
